import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { DealerSoldRow } from '../../types/bruktbilfinansiering'

const PAGE_SIZE = 50

/** Date only. hendelse_tidspunkt carries a time-of-day and the rows are ordered
 *  by it, but the time is noise in the grid — only the date is shown. Sorting
 *  still uses the full ISO string, so intra-day order survives. */
function fmtDateOnly(iso: string): string {
  const [y, m, d] = iso.split('T')[0].split('-')
  return `${d}.${m}.${y}`
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

/** Month.year, matching how "Med finansieringsdata" renders the same column.
 *  Both tabs read forstegang_reg_dato, so they must also print it the same way
 *  — otherwise the same car reads as 04.2018 in one tab and 20.04.2018 in the
 *  other, which looks like disagreeing data rather than two formats. */
function fmtMonthYear(iso: string | null): string {
  if (!iso) return '—'
  const [y, m] = iso.split('-')
  return `${m}.${y}`
}

/** BILBRANSJE_TO_PERSON -> "Bilbransje → Person" */
function fmtKlasse(klasse: string | null): string {
  if (!klasse) return '—'
  return klasse
    .split('_TO_')
    .map(part => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' → ')
}

function buyerKindLabel(row: DealerSoldRow, t: TFunction<'bbf'>): string {
  if (row.buyer_kind === 'org') return row.buyer_org_navn ?? t('soldRows.unfinanced.company')
  if (row.buyer_kind === 'person') return t('shared.privatePerson')
  return '—'
}

type SortKey = 'hendelse_tidspunkt' | 'kjennemerke' | 'merke' | 'modell' | 'klasse'
  | 'forstegang_reg' | 'registrert_pa_eier' | 'buyer_kind' | 'buyer_kommune'

interface Props {
  rows: DealerSoldRow[]
  loading: boolean
  error: string | null
  dataThrough: string | null
  contextInfo?: string
}

export default function DealerSoldRowsUnfinancedSection({
  rows, loading, error, dataThrough, contextInfo,
}: Props) {
  const { t } = useTranslation('bbf')
  const [page, setPage] = useState(1)
  const [filterMerke, setFilterMerke] = useState('')
  const [filterKjoper, setFilterKjoper] = useState('')
  const [filterKlasse, setFilterKlasse] = useState('')

  // null = keep the server's order, which is hendelse_tidspunkt DESC (AC4).
  const [sortCol, setSortCol] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  function handleSort(col: SortKey) {
    if (sortCol === col) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  const uniqueMerker = useMemo(
    () => [...new Set(rows.filter(r => r.merke).map(r => r.merke!))].sort(),
    [rows],
  )
  const uniqueKlasser = useMemo(
    () => [...new Set(rows.filter(r => r.klasse).map(r => r.klasse!))].sort(),
    [rows],
  )

  const filteredRows = useMemo(() => rows
    .filter(r => !filterMerke || r.merke === filterMerke)
    .filter(r => !filterKjoper || r.buyer_kind === filterKjoper)
    .filter(r => !filterKlasse || r.klasse === filterKlasse),
    [rows, filterMerke, filterKjoper, filterKlasse])

  const sortedRows = useMemo(() => {
    if (!sortCol) return filteredRows
    return [...filteredRows].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filteredRows, sortCol, sortDir])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE))
  useEffect(() => { setPage(1) }, [rows, filterMerke, filterKjoper, filterKlasse, sortCol, sortDir])
  const safePage = Math.min(page, totalPages)
  const pageRows = sortedRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const rowOffset = (safePage - 1) * PAGE_SIZE

  const hasActiveFilter = !!(filterMerke || filterKjoper || filterKlasse)

  function SortHeader({ col, label }: { col: SortKey; label: string }) {
    return (
      <th className="py-2 pr-3 whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort(col)}>
        {label} {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </th>
    )
  }

  return (
    <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <header className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('soldRows.unfinanced.intro')}{' '}
          <strong>{dataThrough ? fmtDate(dataThrough) : '—'}</strong>
        </p>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {filteredRows.length < rows.length
            ? t('soldRows.unfinanced.carsCountOf', { n: filteredRows.length, total: rows.length })
            : t('soldRows.unfinanced.carsCount', { n: filteredRows.length })}
        </span>
      </header>

      {contextInfo && (
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-xs text-blue-700 dark:text-blue-300 mb-3">
          {contextInfo}
        </div>
      )}

      {loading && <p className="text-gray-500 py-6">{t('soldRows.unfinanced.loading')}</p>}
      {error && <p className="text-red-600 dark:text-red-400 py-6">{error}</p>}

      {!loading && !error && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <select value={filterMerke} onChange={e => setFilterMerke(e.target.value)}
              className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800">
              <option value="">{t('shared.allBrands')}</option>
              {uniqueMerker.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={filterKlasse} onChange={e => setFilterKlasse(e.target.value)}
              className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800">
              <option value="">{t('soldRows.unfinanced.allClasses')}</option>
              {uniqueKlasser.map(k => <option key={k} value={k}>{fmtKlasse(k)}</option>)}
            </select>
            <select value={filterKjoper} onChange={e => setFilterKjoper(e.target.value)}
              className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800">
              <option value="">{t('shared.allBuyers')}</option>
              <option value="person">{t('shared.privatePerson')}</option>
              <option value="org">{t('soldRows.unfinanced.company')}</option>
            </select>
            {hasActiveFilter && (
              <button onClick={() => { setFilterMerke(''); setFilterKjoper(''); setFilterKlasse('') }}
                className="text-xs text-primary-600 hover:underline">
                {t('shared.reset')}
              </button>
            )}
          </div>

          {sortedRows.length === 0 ? (
            <p className="text-gray-500 py-6">{t('soldRows.unfinanced.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                    {/* Column order mirrors "Med finansieringsdata": the shared
                        columns sit in the same positions, and the ones unique to
                        this tab fill the gap the loan columns leave behind. */}
                    <SortHeader col="hendelse_tidspunkt" label={t('shared.date')} />
                    <SortHeader col="merke" label={t('shared.brand')} />
                    <SortHeader col="modell" label={t('shared.model')} />
                    <SortHeader col="forstegang_reg" label={t('shared.firstReg')} />
                    <SortHeader col="kjennemerke" label={t('soldRows.unfinanced.regNo')} />
                    <SortHeader col="klasse" label={t('soldRows.unfinanced.class')} />
                    <SortHeader col="registrert_pa_eier" label={t('soldRows.unfinanced.registeredToOwner')} />
                    <SortHeader col="buyer_kind" label={t('shared.buyer')} />
                    {/* No buyer age or postnummer: beside the plate they identify
                        the buyer (#912). */}
                    <SortHeader col="buyer_kommune" label={t('shared.kommune')} />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, i) => (
                    <tr
                      key={`${row.hendelse_tidspunkt}-${row.kjennemerke ?? ''}-${rowOffset + i}`}
                      className="border-b border-gray-100 dark:border-gray-700"
                    >
                      <td className="py-1 pr-3 whitespace-nowrap">{fmtDateOnly(row.hendelse_tidspunkt)}</td>
                      <td className="pr-3">{row.merke ?? '—'}</td>
                      <td className="pr-3">{row.modell ?? '—'}</td>
                      <td className="pr-3 whitespace-nowrap">{fmtMonthYear(row.forstegang_reg)}</td>
                      <td className="pr-3 whitespace-nowrap font-mono text-xs">{row.kjennemerke ?? '—'}</td>
                      <td className="pr-3 whitespace-nowrap text-xs" title={row.klasse ?? undefined}>{fmtKlasse(row.klasse)}</td>
                      <td className="pr-3 whitespace-nowrap">{fmtDate(row.registrert_pa_eier)}</td>
                      <td className="pr-3">{buyerKindLabel(row, t)}</td>
                      <td>{row.buyer_kommune ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-sm text-gray-600 dark:text-gray-400">
              <span>
                {t('shared.pagination', { page: safePage, total: totalPages, from: rowOffset + 1, to: Math.min(safePage * PAGE_SIZE, sortedRows.length), rows: sortedRows.length })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('shared.prev')}
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('shared.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}
